import { useState } from 'react'
import * as stylex from '@stylexjs/stylex'

import Button from '../../components/Button'
import Card from '../../components/Card'
import EmptyState from '../../components/EmptyState'
import { Input } from '../../components/FormControls'
import PageHeader from '../../components/PageHeader'
import type { AdminAccount } from '../../lib/ledger'

const styles = stylex.create({
  page: { display: 'flex', flexDirection: 'column', rowGap: '24px', paddingBottom: '32px' },
  header: { display: 'flex', flexDirection: 'column', rowGap: '6px' },
  eyebrow: { color: '#52735a', fontSize: '12px', fontWeight: 650, textTransform: 'uppercase', letterSpacing: '.08em' },
  title: { fontSize: '28px', fontWeight: 650 },
  muted: { color: '#748077', fontSize: '13px', lineHeight: '1.35rem' },
  card: { overflow: 'hidden', borderWidth: 1, borderColor: '#e1e8df', borderRadius: '1rem', backgroundColor: '#fff' },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', borderBottomWidth: 1, borderColor: '#edf0eb', padding: '18px 20px', ':last-child': { borderBottomWidth: 0 }, '@media (max-width: 720px)': { flexDirection: 'column', alignItems: 'stretch' } },
  identity: { minWidth: '0px', display: 'flex', flexDirection: 'column', rowGap: '4px' },
  name: { fontSize: '15px', fontWeight: 650, color: '#29322b' },
  email: { color: '#748077', fontSize: '12px' },
  meta: { color: '#66756a', fontSize: '11px' },
  actions: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' },
  input: { height: '38px', width: '180px', borderWidth: 1, borderColor: '#dbe2d9', borderRadius: '.65rem', paddingInline: '10px', fontSize: '12px', outline: 'none', ':focus': { borderColor: '#5b9a6c', boxShadow: '0 0 0 3px rgb(91 154 108 / 12%)' } },
  button: { height: '38px', borderRadius: '.65rem', backgroundColor: '#408b56', paddingInline: '12px', color: '#fff', fontSize: '12px', fontWeight: 650, ':hover': { backgroundColor: '#347c48' }, ':disabled': { cursor: 'not-allowed', opacity: .55 } },
  secondary: { backgroundColor: '#f2f7f1', color: '#477452', ':hover': { backgroundColor: '#e6f0e4' } },
  admin: { color: '#397a4b', fontWeight: 650 },
  error: { borderRadius: '.75rem', backgroundColor: '#fff3ed', padding: '10px 12px', color: '#9b491e', fontSize: '12px' },
  empty: { padding: '32px', textAlign: 'center' },
})

type AdminPageProps = {
  accounts: AdminAccount[]
  loading: boolean
  error: string
  onRetry: () => void
  onResetPassword: (accountID: string, password: string) => Promise<void>
  onRevokeSessions: (accountID: string) => Promise<void>
}

export default function AdminPage({ accounts, loading, error, onRetry, onResetPassword, onRevokeSessions }: AdminPageProps) {
  const [passwords, setPasswords] = useState<Record<string, string>>({})
  const [working, setWorking] = useState('')
  const [message, setMessage] = useState('')

  async function resetPassword(account: AdminAccount) {
    const password = passwords[account.id] ?? ''
    if (password.length < 8) {
      setMessage('Use at least 8 characters for the new password.')
      return
    }
    setWorking(`${account.id}:reset`); setMessage('')
    try {
      await onResetPassword(account.id, password)
      setPasswords((current) => ({ ...current, [account.id]: '' }))
      setMessage(`Password reset for ${account.email}. Their active sessions were revoked.`)
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : 'Could not reset this password.')
    } finally { setWorking('') }
  }

  async function revokeSessions(account: AdminAccount) {
    setWorking(`${account.id}:revoke`); setMessage('')
    try { await onRevokeSessions(account.id); setMessage(`Active sessions revoked for ${account.email}.`) } catch (reason) { setMessage(reason instanceof Error ? reason.message : 'Could not revoke sessions.') } finally { setWorking('') }
  }

  return <div {...stylex.props(styles.page)}>
    <PageHeader eyebrow="Administrator" subtitle="View every account and help recover access without exposing passwords or passkey secrets." title="Account management" />
    {error && <p {...stylex.props(styles.error)} role="alert">{error} <Button onClick={onRetry} size="sm" variant="ghost">Try again</Button></p>}
    {message && <p {...stylex.props(styles.muted)} role="status">{message}</p>}
    <Card aria-label="User accounts" padding="none">
      {loading ? <EmptyState description="Loading accounts…" /> : accounts.length === 0 ? <EmptyState description="No accounts found." /> : accounts.map((account) => <article {...stylex.props(styles.row)} key={account.id}>
        <div {...stylex.props(styles.identity)}><p {...stylex.props(styles.name)}>{account.displayName || 'Unnamed account'} {account.role === 'admin' && <span {...stylex.props(styles.admin)}>(admin)</span>}</p><p {...stylex.props(styles.email)}>{account.email}</p><p {...stylex.props(styles.meta)}>{account.status} · {account.defaultCurrency} · {account.passkeyCount} passkey{account.passkeyCount === 1 ? '' : 's'}</p></div>
        <div {...stylex.props(styles.actions)}><Input aria-label={`New password for ${account.email}`} onChange={(event) => setPasswords((current) => ({ ...current, [account.id]: event.target.value }))} placeholder="New password" style={{ width: 180 }} type="password" value={passwords[account.id] ?? ''} /><Button disabled={working !== ''} onClick={() => void resetPassword(account)} size="sm">Reset password</Button><Button disabled={working !== ''} onClick={() => void revokeSessions(account)} size="sm" variant="secondary">Revoke sessions</Button></div>
      </article>)}
    </Card>
  </div>
}
